import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { nanoid } from 'nanoid';
import {
    AlertTriangleIcon,
    BanIcon,
    CarIcon,
    CheckIcon,
    CrosshairIcon,
    HeartPulseIcon,
    KeyRoundIcon,
    LogOutIcon,
    MinusIcon,
    PackageIcon,
    PencilIcon,
    PlusIcon,
    RefreshCwIcon,
    RotateCcwIcon,
    SearchIcon,
    SnowflakeIcon,
    SparklesIcon,
    Trash2Icon,
    WarehouseIcon,
    XIcon,
} from 'lucide-react';
import type { FcAdminMutationOperation, FcAdminQueryRequest, FcAdminTarget } from '@shared/fcAdminApiTypes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { txToast } from '@/components/TxToaster';
import { cn } from '@/lib/utils';
import { formatFcAdminNotice, isAmbiguousFcAdminActionError, useFcAdminAccess, useFcAdminAction, useFcAdminQuery } from './fcAdmin';
import type { ActionCatalog } from '@/pages/FcAdmin/FcAdminActionsPage';

export type RecordValue = Record<string, unknown>;

export const records = (value: unknown) => Array.isArray(value)
    ? value.filter((entry): entry is RecordValue => !!entry && typeof entry === 'object')
    : [];

export const object = (value: unknown): RecordValue => value && typeof value === 'object' && !Array.isArray(value) ? value as RecordValue : {};

export const text = (value: unknown, fallback = '–') => {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return fallback;
};

export const timestamp = (value: unknown) => {
    if (value === null || value === undefined || value === '') return '–';
    const numeric = Number(value);
    const date = Number.isFinite(numeric)
        ? new Date(numeric < 10_000_000_000 ? numeric * 1000 : numeric)
        : new Date(String(value));
    return Number.isNaN(date.getTime()) ? '–' : date.toLocaleString('de-DE');
};

const euroFormat = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const numberFormat = new Intl.NumberFormat('de-DE');

export const money = (value: unknown) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? euroFormat.format(amount) : '–';
};

export const amountText = (value: unknown) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? numberFormat.format(amount) : '–';
};

const dossierOperations = [
    'player.money',
    'player.coins',
    'player.item',
    'player.weapon',
    'vehicle.give',
    'vehicle.impound',
    'vehicle.delete',
    'vip.update',
    'licenses.update',
    'player.job',
    'player.group',
    'player.status',
    'player.dimension',
    'player.kick',
    'player.freeze',
    'player.heal',
    'player.revive',
    'jail.apply',
    'jail.release',
    'moderation.ban',
    'moderation.warn',
    'moderation.note',
    'moderation.revoke',
] as const satisfies readonly FcAdminMutationOperation[];

export type DossierOperation = typeof dossierOperations[number];

export const onlineOnlyOperations = new Set<FcAdminMutationOperation>([
    'player.kick',
    'player.freeze',
    'player.heal',
    'player.revive',
    'player.status',
    'player.dimension',
    'jail.apply',
    'jail.release',
]);

export type DossierMutation = {
    operation: DossierOperation;
    payload: RecordValue;
    title: string;
    before?: string;
    after?: string;
    danger?: boolean;
    confirm?: boolean;
    reasonKey?: string | null;
};

type PendingMutation = DossierMutation & { reasonDraft: string; notice?: string };

export const createDossierPendingSettler = () => {
    let settle: ((ok: boolean) => void) | undefined;
    return {
        open: () => new Promise<boolean>((resolve) => {
            const previous = settle;
            settle = resolve;
            previous?.(false);
        }),
        settle: (ok: boolean) => {
            const current = settle;
            settle = undefined;
            current?.(ok);
        },
    };
};

export type DossierCacheMode = 'ensure' | 'retry' | 'repair';

export const shouldLoadDossierCache = (entry: { failed: boolean } | undefined, mode: DossierCacheMode) => entry
    ? entry.failed && mode !== 'ensure'
    : mode !== 'repair';

export const nextDossierScopes = <Domain extends string>(
    current: Partial<Record<Domain, string>>,
    domain: Domain,
    scope: string,
    ok: boolean,
    keepOnFailure = false,
): Partial<Record<Domain, string>> => {
    if (ok) return current[domain] === scope ? current : { ...current, [domain]: scope };
    if (keepOnFailure || current[domain] === undefined) return current;
    const next = { ...current };
    delete next[domain];
    return next;
};

type DossierMutationContextValue = {
    target: FcAdminTarget;
    online: boolean;
    reason: string;
    setReason: (value: string) => void;
    allowed: (operation: DossierOperation) => boolean;
    ready: boolean;
    mutate: (mutation: DossierMutation) => Promise<boolean>;
    busy: boolean;
};

const DossierMutationContext = createContext<DossierMutationContextValue | undefined>(undefined);

export const useDossierMutation = () => {
    const value = useContext(DossierMutationContext);
    if (!value) throw new Error('DossierMutationProvider fehlt.');
    return value;
};

const useDossierRunners = () => {
    const runners: Record<DossierOperation, ReturnType<typeof useFcAdminAction>> = {
        'player.money': useFcAdminAction('player.money'),
        'player.coins': useFcAdminAction('player.coins'),
        'player.item': useFcAdminAction('player.item'),
        'player.weapon': useFcAdminAction('player.weapon'),
        'vehicle.give': useFcAdminAction('vehicle.give'),
        'vehicle.impound': useFcAdminAction('vehicle.impound'),
        'vehicle.delete': useFcAdminAction('vehicle.delete'),
        'vip.update': useFcAdminAction('vip.update'),
        'licenses.update': useFcAdminAction('licenses.update'),
        'player.job': useFcAdminAction('player.job'),
        'player.group': useFcAdminAction('player.group'),
        'player.status': useFcAdminAction('player.status'),
        'player.dimension': useFcAdminAction('player.dimension'),
        'player.kick': useFcAdminAction('player.kick'),
        'player.freeze': useFcAdminAction('player.freeze'),
        'player.heal': useFcAdminAction('player.heal'),
        'player.revive': useFcAdminAction('player.revive'),
        'jail.apply': useFcAdminAction('jail.apply'),
        'jail.release': useFcAdminAction('jail.release'),
        'moderation.ban': useFcAdminAction('moderation.ban'),
        'moderation.warn': useFcAdminAction('moderation.warn'),
        'moderation.note': useFcAdminAction('moderation.note'),
        'moderation.revoke': useFcAdminAction('moderation.revoke'),
    };
    return runners;
};

type Option = { value: string; label: string };

type Template = { id: string; label: string; reason: string; hours: number };

type CachedOptions<T> = {
    items: T[];
    error?: string;
    ensure: () => void;
    retry: () => void;
    invalidate: () => void;
};

export type DossierOptions = {
    tiers: CachedOptions<Option>;
    licenseTypes: CachedOptions<Option>;
    templates: CachedOptions<Template>;
};

const DossierOptionsContext = createContext<DossierOptions | undefined>(undefined);

const useDossierOptions = () => {
    const value = useContext(DossierOptionsContext);
    if (!value) throw new Error('DossierMutationProvider fehlt.');
    return value;
};

const vipTierOption = (entry: RecordValue): Option | undefined => typeof entry.value === 'string'
    ? { value: entry.value, label: typeof entry.label === 'string' ? entry.label : entry.value }
    : undefined;

const licenseTypeOption = (entry: RecordValue): Option | undefined => typeof entry.type === 'string'
    ? { value: entry.type, label: typeof entry.label === 'string' ? entry.label : entry.type }
    : undefined;

const banTemplate = (entry: RecordValue): Template | undefined => {
    const label = typeof entry.label === 'string' ? entry.label.trim() : '';
    const reason = typeof entry.reason === 'string' ? entry.reason.trim() : '';
    const hours = Number(entry.durationHours ?? entry.duration_hours ?? entry.duration ?? 0);
    return entry.id !== undefined && label && reason && Number.isFinite(hours)
        ? { id: String(entry.id), label, reason, hours: Math.max(0, Math.floor(hours)) }
        : undefined;
};

const templateRequest: FcAdminQueryRequest = { limit: 100 };

const useCachedOptions = <T,>(
    operation: 'vip.read' | 'licenses.read' | 'moderation.templates',
    mapper: (entry: RecordValue) => T | undefined,
    request?: FcAdminQueryRequest,
): CachedOptions<T> => {
    const { can } = useFcAdminAccess();
    const query = useFcAdminQuery(operation);
    const run = query.run;
    const permitted = can(operation);
    const requested = useRef<{ run: typeof run; failed: boolean }>();
    const load = useCallback((mode: DossierCacheMode) => {
        if (!permitted) return;
        const current = requested.current?.run === run ? requested.current : undefined;
        if (!shouldLoadDossierCache(current, mode)) return;
        const entry = { run, failed: false };
        requested.current = entry;
        void run(request).then((response) => { entry.failed = !response?.ok; });
    }, [permitted, request, run]);
    const ensure = useCallback(() => load('ensure'), [load]);
    const retry = useCallback(() => load('retry'), [load]);
    const invalidate = useCallback(() => { requested.current = undefined; }, []);
    const data = query.data;
    const items = useMemo(() => records(data?.items).flatMap((entry) => {
        const mapped = mapper(entry);
        return mapped === undefined ? [] : [mapped];
    }), [data, mapper]);
    const error = permitted ? query.error : undefined;
    return useMemo(() => ({ items, error, ensure, retry, invalidate }), [ensure, error, invalidate, items, retry]);
};

export const useDossierOptionCache = (): DossierOptions => {
    const tiers = useCachedOptions('vip.read', vipTierOption);
    const licenseTypes = useCachedOptions('licenses.read', licenseTypeOption);
    const templates = useCachedOptions('moderation.templates', banTemplate, templateRequest);
    return useMemo(() => ({ tiers, licenseTypes, templates }), [licenseTypes, templates, tiers]);
};

export function DossierMutationProvider({ target, targetLabel, options, onSuccess, children }: {
    target: FcAdminTarget;
    targetLabel: string;
    options: DossierOptions;
    onSuccess: (operation: DossierOperation) => void;
    children: ReactNode;
}) {
    const { capabilities, mutationReady } = useFcAdminAccess();
    const runners = useDossierRunners();
    const runnersRef = useRef(runners);
    runnersRef.current = runners;
    const [reason, setReason] = useState('');
    const [pending, setPending] = useState<PendingMutation>();
    const [busy, setBusy] = useState(false);
    const settler = useRef(createDossierPendingSettler());
    const online = !!target.connectionGeneration;
    const targetScope = `${target.publicId}:${target.connectionGeneration ?? target.characterRef ?? ''}`;

    useEffect(() => {
        const current = settler.current;
        setPending(undefined);
        setBusy(false);
        return () => current.settle(false);
    }, [targetScope]);

    const openPending = useCallback((next: PendingMutation) => {
        const settled = settler.current.open();
        setPending(next);
        return settled;
    }, []);

    const allowed = useCallback((operation: DossierOperation) => capabilities.has(operation)
        && (!onlineOnlyOperations.has(operation) || online), [capabilities, online]);

    const execute = useCallback(async (mutation: DossierMutation, reasonText: string) => {
        const reasonKey = mutation.reasonKey === undefined ? 'reason' : mutation.reasonKey;
        const payload = reasonKey ? { ...mutation.payload, [reasonKey]: reasonText } : mutation.payload;
        setBusy(true);
        try {
            const response = await runnersRef.current[mutation.operation]({
                payload,
                target,
                idempotencyKey: `${mutation.operation}:${nanoid()}`,
            });
            if (!response) {
                return { ok: false, retry: true, notice: 'Die Antwort ist ausgeblieben. Ein erneuter Versuch verwendet denselben Idempotenzschlüssel.' };
            }
            if (response.ok) {
                txToast.success({ title: mutation.title, msg: `Ausgeführt · ${response.correlationId}` });
                onSuccess(mutation.operation);
                return { ok: true, retry: false };
            }
            const notice = formatFcAdminNotice(response);
            const retry = isAmbiguousFcAdminActionError(response.code);
            if (!retry) txToast.error({ title: mutation.title, msg: notice });
            return { ok: false, retry, notice };
        } finally {
            setBusy(false);
        }
    }, [onSuccess, target]);

    const mutate = useCallback(async (mutation: DossierMutation) => {
        if (!mutationReady || !allowed(mutation.operation)) {
            txToast.error({ title: mutation.title, msg: 'Diese Änderung ist für dein Konto oder in diesem Zustand gesperrt.' });
            return false;
        }
        const reasonKey = mutation.reasonKey === undefined ? 'reason' : mutation.reasonKey;
        const reasonText = reason.trim();
        if (mutation.danger || mutation.confirm || (reasonKey && reasonText.length < 3)) {
            return openPending({ ...mutation, reasonDraft: reasonText });
        }
        const result = await execute(mutation, reasonText);
        if (!result.ok && result.retry) return openPending({ ...mutation, reasonDraft: reasonText, notice: result.notice });
        return result.ok;
    }, [allowed, execute, mutationReady, openPending, reason]);

    const confirmPending = async () => {
        if (!pending) return;
        const reasonKey = pending.reasonKey === undefined ? 'reason' : pending.reasonKey;
        const reasonText = pending.reasonDraft.trim();
        if (reasonKey && reasonText.length < 3) {
            setPending({ ...pending, notice: 'Bitte eine Begründung mit mindestens drei Zeichen angeben.' });
            return;
        }
        const result = await execute(pending, reasonText);
        if (result.ok) {
            if (reasonKey && !reason.trim()) setReason(reasonText);
            setPending(undefined);
            settler.current.settle(true);
        } else if (result.retry) {
            setPending({ ...pending, notice: result.notice });
        } else {
            setPending(undefined);
            settler.current.settle(false);
        }
    };

    const cancelPending = () => {
        setPending(undefined);
        settler.current.settle(false);
    };

    const value = useMemo<DossierMutationContextValue>(() => ({
        target,
        online,
        reason,
        setReason,
        allowed,
        ready: mutationReady,
        mutate,
        busy,
    }), [allowed, busy, mutate, mutationReady, online, reason, target]);

    const needsReason = pending ? (pending.reasonKey === undefined ? 'reason' : pending.reasonKey) !== null : false;

    return (
        <DossierMutationContext.Provider value={value}>
            <DossierOptionsContext.Provider value={options}>{children}</DossierOptionsContext.Provider>
            <AlertDialog open={!!pending} onOpenChange={(open) => { if (!open && !busy) cancelPending(); }}>
                <AlertDialogContent variant={pending?.danger ? 'danger' : 'default'} className="max-w-[520px]">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{pending?.title}</AlertDialogTitle>
                        <AlertDialogDescription>
                            Ziel: {targetLabel} · FC-ID {target.publicId}. Die Änderung wird serverseitig direkt vor dem Commit erneut geprüft.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    {pending?.before || pending?.after ? (
                        <div className="fc-action-diff">
                            <div><span>Vorher</span><strong>{pending.before ?? '–'}</strong></div>
                            <div><span>Nachher</span><strong>{pending.after ?? '–'}</strong></div>
                        </div>
                    ) : null}
                    {needsReason ? (
                        <div className="fc-field">
                            <Label htmlFor="fc-dossier-confirm-reason">Begründung</Label>
                            <Input
                                id="fc-dossier-confirm-reason"
                                autoFocus
                                value={pending?.reasonDraft ?? ''}
                                placeholder="Warum wird das geändert?"
                                disabled={busy}
                                onChange={(event) => pending && setPending({ ...pending, reasonDraft: event.target.value, notice: undefined })}
                                onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void confirmPending(); } }}
                            />
                        </div>
                    ) : null}
                    {pending?.notice ? <div className="fc-notice warn">{pending.notice}</div> : null}
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy}>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            className={pending?.danger ? 'border-fc-red/55 bg-fc-red/[0.22] text-fc-red-bright hover:bg-fc-red/[0.32]' : undefined}
                            disabled={busy}
                            onClick={(event) => { event.preventDefault(); void confirmPending(); }}
                        >
                            {busy ? 'Wird geprüft …' : 'Jetzt ausführen'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </DossierMutationContext.Provider>
    );
}

export function DossierReasonBar() {
    const { reason, setReason, ready } = useDossierMutation();
    const valid = reason.trim().length >= 3;
    return (
        <div className={cn('fc-dossier-reason', valid && 'is-valid')}>
            <PencilIcon />
            <label htmlFor="fc-dossier-reason">Begründung</label>
            <input
                id="fc-dossier-reason"
                value={reason}
                maxLength={240}
                placeholder="Gilt für alle Änderungen in dieser Akte, z. B. Ticket #812 oder Rückerstattung"
                onChange={(event) => setReason(event.target.value)}
            />
            <span>{!ready ? 'Änderungen gesperrt' : valid ? 'Bereit' : 'mind. 3 Zeichen'}</span>
        </div>
    );
}

export function DomainFailure({ message, onRetry }: { message?: string; onRetry?: () => void }) {
    if (!message) return null;
    return <div className="fc-dossier-domain-error" role="alert"><AlertTriangleIcon /><span>{message}</span>{onRetry ? <Button type="button" size="xs" variant="secondary" onClick={onRetry}><RefreshCwIcon />Erneut laden</Button> : null}</div>;
}

function Glare({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
    return <div className={cn('fc-glare', className)} {...props}>{children}</div>;
}

export { Glare };

export function DossierSection({ icon, title, meta, actions, children, className }: {
    icon: ReactNode;
    title: string;
    meta?: ReactNode;
    actions?: ReactNode;
    children: ReactNode;
    className?: string;
}) {
    return (
        <Glare className={cn('fc-dossier-section', className)}>
            <header>
                <span className="fc-dossier-section-icon">{icon}</span>
                <strong>{title}</strong>
                {meta ? <em>{meta}</em> : null}
                <i />
                {actions}
            </header>
            <div className="fc-dossier-section-body">{children}</div>
        </Glare>
    );
}

const accountLabels: Record<string, string> = { money: 'Bargeld', bank: 'Bank', black_money: 'Schwarzgeld' };

type Mode = 'add' | 'remove' | 'set';

const applyMode = (before: number, mode: Mode, amount: number) => mode === 'add'
    ? before + amount
    : mode === 'remove' ? Math.max(0, before - amount) : amount;

export function AccountTile({ account, value, coins }: { account: string; value: unknown; coins?: boolean }) {
    const { mutate, allowed, ready, busy } = useDossierMutation();
    const operation: DossierOperation = coins ? 'player.coins' : 'player.money';
    const editable = allowed(operation) && value !== undefined && ready;
    const current = Number(value ?? 0);
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState<Mode>('add');
    const [amount, setAmount] = useState('');
    const label = coins ? 'Coins' : accountLabels[account] ?? account;
    const format = (input: number) => coins ? amountText(input) : money(input);
    const parsed = Math.max(0, Math.floor(Number(amount.replace(/\./g, '').replace(',', '.')) || 0));
    const next = applyMode(current, mode, parsed);

    const reset = () => { setOpen(false); setAmount(''); setMode('add'); };

    const submit = async () => {
        if (!parsed && mode !== 'set') return;
        const ok = await mutate({
            operation,
            payload: coins ? { mode, amount: parsed } : { account, mode, amount: parsed },
            title: `${label} ${mode === 'add' ? 'erhöhen' : mode === 'remove' ? 'verringern' : 'setzen'}`,
            before: format(current),
            after: format(next),
            danger: next < current,
        });
        if (ok) reset();
    };

    return (
        <Glare className={cn('fc-dossier-account', open && 'is-open', coins && 'is-coins')}>
            <div className="fc-dossier-account-head">
                <span>{label}</span>
                {editable && !open ? <button type="button" className="fc-dossier-icon-btn" aria-label={`${label} bearbeiten`} onClick={() => setOpen(true)}><PencilIcon /></button> : null}
            </div>
            <strong className={cn(!coins && account === 'black_money' && current > 0 && 'is-warn')}>{format(current)}</strong>
            {open ? (
                <form className="fc-dossier-account-editor" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
                    <div className="fc-segment">
                        {(['add', 'remove', 'set'] as Mode[]).map((entry) => (
                            <button key={entry} type="button" className={cn(mode === entry && 'active', entry === 'remove' && 'danger')} onClick={() => setMode(entry)}>
                                {entry === 'add' ? <PlusIcon /> : entry === 'remove' ? <MinusIcon /> : '='}
                            </button>
                        ))}
                    </div>
                    <Input autoFocus inputMode="numeric" value={amount} placeholder="Betrag" onChange={(event) => setAmount(event.target.value.replace(/[^\d.,]/g, ''))} onKeyDown={(event) => { if (event.key === 'Escape') reset(); }} />
                    <div className="fc-dossier-chips">
                        {(coins ? [10, 100, 1000] : [1000, 10000, 100000]).map((step) => (
                            <button key={step} type="button" onClick={() => setAmount(String(parsed + step))}>+{amountText(step)}</button>
                        ))}
                    </div>
                    <div className="fc-dossier-account-preview">
                        <span>{format(current)}</span><b>→</b><span className={cn(next < current ? 'is-down' : next > current ? 'is-up' : undefined)}>{format(next)}</span>
                    </div>
                    <div className="fc-dossier-inline-actions">
                        <Button type="submit" size="sm" disabled={busy || (!parsed && mode !== 'set')}><CheckIcon />Übernehmen</Button>
                        <Button type="button" size="sm" variant="ghost" onClick={reset}><XIcon /></Button>
                    </div>
                </form>
            ) : null}
        </Glare>
    );
}

function CatalogPicker({ options, value, onChange, placeholder, id }: {
    options: { value: string; label: string }[];
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    id: string;
}) {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const selected = options.find((option) => option.value === value);
    const filtered = useMemo(() => {
        const needle = query.trim().toLocaleLowerCase('de-DE');
        const list = needle
            ? options.filter((option) => option.label.toLocaleLowerCase('de-DE').includes(needle) || option.value.toLocaleLowerCase('de-DE').includes(needle))
            : options;
        return list.slice(0, 40);
    }, [options, query]);
    return (
        <div className="fc-dossier-picker" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false); }}>
            <SearchIcon />
            <input
                id={id}
                value={open ? query : selected ? selected.label : query}
                placeholder={placeholder}
                autoComplete="off"
                onFocus={() => { setOpen(true); setQuery(''); }}
                onChange={(event) => { setQuery(event.target.value); setOpen(true); if (value) onChange(''); }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' && filtered[0]) { event.preventDefault(); onChange(filtered[0].value); setOpen(false); }
                    if (event.key === 'Escape') setOpen(false);
                }}
            />
            {open ? (
                <div className="fc-dossier-picker-list" role="listbox">
                    {filtered.length ? filtered.map((option) => (
                        <button key={option.value} type="button" role="option" aria-selected={option.value === value} onMouseDown={(event) => event.preventDefault()} onClick={() => { onChange(option.value); setOpen(false); setQuery(''); }}>
                            <span>{option.label}</span><code>{option.value}</code>
                        </button>
                    )) : <p>Kein Treffer im Serverkatalog.</p>}
                </div>
            ) : null}
        </div>
    );
}

export function InventorySection({ data, catalog }: { data: RecordValue; catalog: ActionCatalog }) {
    const { mutate, allowed, ready, busy } = useDossierMutation();
    const editable = allowed('player.item') && ready;
    const inventory = records(data.inventory);
    const [filter, setFilter] = useState('');
    const [drafts, setDrafts] = useState<Record<string, string>>({});
    const [newItem, setNewItem] = useState('');
    const [newAmount, setNewAmount] = useState('1');
    const itemOptions = useMemo(() => (catalog.items ?? []).map((entry) => ({ value: entry.name, label: entry.label })), [catalog.items]);
    const visible = filter.trim()
        ? inventory.filter((item) => `${text(item.label, '')} ${text(item.name, '')}`.toLocaleLowerCase('de-DE').includes(filter.trim().toLocaleLowerCase('de-DE')))
        : inventory;
    const weight = data.weight !== undefined ? `${text(data.weight, '0')} / ${text(data.maximumWeight, '–')} kg` : undefined;

    const commit = async (item: RecordValue, nextCount: number) => {
        const name = text(item.name, '');
        const count = Number(item.count ?? item.amount ?? 0);
        if (!name || nextCount === count || nextCount < 0) return;
        const label = text(item.label ?? item.name);
        const ok = await mutate({
            operation: 'player.item',
            payload: { item: name, mode: 'set', amount: nextCount },
            title: nextCount === 0 ? `${label} entfernen` : `${label} auf ${nextCount} setzen`,
            before: `${label} · ${count}`,
            after: nextCount === 0 ? `${label} entfernt` : `${label} · ${nextCount}`,
            danger: nextCount < count,
        });
        if (ok) setDrafts((current) => { const copy = { ...current }; delete copy[name]; return copy; });
    };

    const add = async () => {
        const amount = Math.max(1, Math.floor(Number(newAmount) || 0));
        const entry = catalog.items?.find((item) => item.name === newItem);
        if (!entry) return;
        const existing = inventory.find((item) => item.name === entry.name);
        const before = Number(existing?.count ?? existing?.amount ?? 0);
        const ok = await mutate({
            operation: 'player.item',
            payload: { item: entry.name, mode: 'add', amount },
            title: `${entry.label} hinzufügen`,
            before: `${entry.label} · ${before}`,
            after: `${entry.label} · ${before + amount}`,
        });
        if (ok) { setNewItem(''); setNewAmount('1'); }
    };

    return (
        <DossierSection
            icon={<PackageIcon />}
            title="Inventar"
            meta={weight ?? `${inventory.length} Positionen`}
            actions={inventory.length > 6 ? <div className="fc-dossier-filter"><SearchIcon /><input value={filter} placeholder="Filtern" onChange={(event) => setFilter(event.target.value)} /></div> : null}
        >
            {editable ? (
                <form className="fc-dossier-add" onSubmit={(event) => { event.preventDefault(); void add(); }}>
                    <CatalogPicker id="fc-dossier-add-item" options={itemOptions} value={newItem} onChange={setNewItem} placeholder="Item suchen und hinzufügen …" />
                    <Input className="fc-dossier-add-amount" inputMode="numeric" value={newAmount} onChange={(event) => setNewAmount(event.target.value.replace(/\D/g, ''))} aria-label="Menge" />
                    <Button type="submit" size="sm" disabled={busy || !newItem}><PlusIcon />Hinzufügen</Button>
                </form>
            ) : null}
            <div className="fc-dossier-rows">
                {visible.map((item, index) => {
                    const name = text(item.name, '');
                    const count = Number(item.count ?? item.amount ?? 0);
                    const draftValue = drafts[name];
                    const draft = draftValue === undefined ? count : Math.max(0, Math.floor(Number(draftValue) || 0));
                    const dirty = draftValue !== undefined && draft !== count;
                    const setDraft = (value: number | string) => setDrafts((current) => ({ ...current, [name]: String(value) }));
                    return (
                        <div key={`${name}:${index}`} className={cn('fc-dossier-row', dirty && 'is-dirty')}>
                            <span className="fc-dossier-row-glyph">{text(item.label ?? item.name, '?').slice(0, 1).toUpperCase()}</span>
                            <div className="fc-dossier-row-copy"><strong>{text(item.label ?? item.name)}</strong><small>{name}</small></div>
                            {editable ? (
                                <div className="fc-dossier-stepper">
                                    <button type="button" aria-label="Weniger" onClick={() => setDraft(Math.max(0, draft - 1))}><MinusIcon /></button>
                                    <input inputMode="numeric" value={draftValue ?? String(count)} onChange={(event) => setDraft(event.target.value.replace(/\D/g, ''))} onKeyDown={(event) => { if (event.key === 'Enter') void commit(item, draft); if (event.key === 'Escape') setDrafts((current) => { const copy = { ...current }; delete copy[name]; return copy; }); }} aria-label={`Menge ${text(item.label ?? item.name)}`} />
                                    <button type="button" aria-label="Mehr" onClick={() => setDraft(draft + 1)}><PlusIcon /></button>
                                </div>
                            ) : <span className="fc-id-chip">{count}</span>}
                            {editable ? (
                                <div className="fc-dossier-row-actions">
                                    {dirty ? <>
                                        <button type="button" className="fc-dossier-icon-btn is-accept" aria-label="Übernehmen" disabled={busy} onClick={() => void commit(item, draft)}><CheckIcon /></button>
                                        <button type="button" className="fc-dossier-icon-btn" aria-label="Zurücksetzen" onClick={() => setDrafts((current) => { const copy = { ...current }; delete copy[name]; return copy; })}><RotateCcwIcon /></button>
                                    </> : <button type="button" className="fc-dossier-icon-btn is-danger" aria-label="Entfernen" disabled={busy} onClick={() => void commit(item, 0)}><Trash2Icon /></button>}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
                {!visible.length ? <div className="fc-dossier-empty">{inventory.length ? 'Kein Item passt zum Filter.' : 'Inventar ist leer.'}</div> : null}
            </div>
        </DossierSection>
    );
}

export function WeaponSection({ data, catalog }: { data: RecordValue; catalog: ActionCatalog }) {
    const { mutate, allowed, ready, busy } = useDossierMutation();
    const editable = allowed('player.weapon') && data.weaponMode === 'instances' && ready;
    const weapons = records(data.weapons ?? data.uidWeapons);
    const [weapon, setWeapon] = useState('');
    const [ammo, setAmmo] = useState('');
    const weaponOptions = useMemo(() => (catalog.weapons ?? []).map((entry) => ({ value: entry.name, label: entry.label })), [catalog.weapons]);
    const catalogWeapon = catalog.weapons?.find((entry) => entry.name === weapon);

    const add = async () => {
        if (!catalogWeapon) return;
        const ammoValue = ammo.trim() === '' ? catalogWeapon.defaultAmmo : Math.min(catalogWeapon.ammoMax, Math.max(catalogWeapon.ammoMin, Math.floor(Number(ammo) || 0)));
        const ok = await mutate({
            operation: 'player.weapon',
            payload: { mode: 'add', weapon: catalogWeapon.name, ammo: ammoValue },
            title: `${catalogWeapon.label} hinzufügen`,
            before: `${weapons.length} UID-Waffen`,
            after: `${weapons.length + 1} UID-Waffen · neu: ${catalogWeapon.label} (${ammoValue} Schuss)`,
        });
        if (ok) { setWeapon(''); setAmmo(''); }
    };

    const remove = (entry: RecordValue) => {
        const revision = Number(entry.projectionRevision ?? entry.revision);
        if (!Number.isSafeInteger(revision) || revision < 0) {
            txToast.error({ title: 'Waffe entfernen', msg: 'Die Waffeninstanz besitzt keinen aktuellen Revisionsstand. Bitte die Akte neu laden.' });
            return;
        }
        const label = text(entry.label ?? entry.name, 'UID-Waffe');
        void mutate({
            operation: 'player.weapon',
            payload: { mode: 'remove', uid: text(entry.uid, ''), expectedRevision: revision },
            title: `${label} entfernen`,
            before: `${label} · UID ${text(entry.uid)}`,
            after: 'Waffeninstanz entfernt',
            danger: true,
        });
    };

    return (
        <DossierSection icon={<CrosshairIcon />} title="UID-Waffen" meta={data.weaponMode === 'instances' ? `${weapons.length} Instanzen` : 'Klassischer Modus'}>
            {editable ? (
                <form className="fc-dossier-add" onSubmit={(event) => { event.preventDefault(); void add(); }}>
                    <CatalogPicker id="fc-dossier-add-weapon" options={weaponOptions} value={weapon} onChange={(value) => { setWeapon(value); setAmmo(''); }} placeholder="Waffe suchen …" />
                    <Input className="fc-dossier-add-amount" inputMode="numeric" value={ammo} placeholder={catalogWeapon ? String(catalogWeapon.defaultAmmo) : 'Munition'} onChange={(event) => setAmmo(event.target.value.replace(/\D/g, ''))} aria-label="Munition" />
                    <Button type="submit" size="sm" disabled={busy || !catalogWeapon}><PlusIcon />Ausgeben</Button>
                </form>
            ) : null}
            <div className="fc-dossier-rows">
                {weapons.map((entry, index) => (
                    <div key={`${text(entry.uid, '')}:${index}`} className="fc-dossier-row">
                        <span className="fc-dossier-row-glyph"><CrosshairIcon /></span>
                        <div className="fc-dossier-row-copy"><strong>{text(entry.label ?? entry.name)}</strong><small className="fc-mono">{text(entry.uid)}</small></div>
                        {entry.ammo !== undefined ? <span className="fc-pill">{amountText(entry.ammo)} Schuss</span> : null}
                        {editable ? <div className="fc-dossier-row-actions"><button type="button" className="fc-dossier-icon-btn is-danger" aria-label="Entfernen" disabled={busy} onClick={() => remove(entry)}><Trash2Icon /></button></div> : null}
                    </div>
                ))}
                {!weapons.length ? <div className="fc-dossier-empty">Keine UID-Waffen.</div> : null}
            </div>
        </DossierSection>
    );
}

export function VehicleSection({ data, catalog }: { data: RecordValue; catalog: ActionCatalog }) {
    const { mutate, allowed, ready, busy } = useDossierMutation();
    const vehicles = records(data.vehicles);
    const [model, setModel] = useState('');
    const vehicleOptions = useMemo(() => (catalog.vehicles ?? []).map((entry) => ({ value: entry.model, label: entry.label })), [catalog.vehicles]);
    const catalogLabels = useMemo(() => new Map((catalog.vehicles ?? []).map((entry) => [entry.model.toLowerCase(), entry.label])), [catalog.vehicles]);
    const canGive = allowed('vehicle.give') && ready;
    const canImpound = allowed('vehicle.impound') && ready;
    const canDelete = allowed('vehicle.delete') && ready;

    const give = async () => {
        const entry = catalog.vehicles?.find((vehicle) => vehicle.model === model);
        if (!entry) return;
        const ok = await mutate({
            operation: 'vehicle.give',
            payload: { model: entry.model, vehicleType: entry.type },
            title: `${entry.label} vergeben`,
            before: `${vehicles.length} Fahrzeuge`,
            after: `${vehicles.length + 1} Fahrzeuge · neu: ${entry.label}`,
        });
        if (ok) setModel('');
    };

    return (
        <DossierSection icon={<CarIcon />} title="Fahrzeuge" meta={`${vehicles.length} im Besitz`}>
            {canGive ? (
                <form className="fc-dossier-add" onSubmit={(event) => { event.preventDefault(); void give(); }}>
                    <CatalogPicker id="fc-dossier-add-vehicle" options={vehicleOptions} value={model} onChange={setModel} placeholder="Fahrzeugmodell suchen …" />
                    <Button type="submit" size="sm" disabled={busy || !model}><PlusIcon />Vergeben</Button>
                </form>
            ) : null}
            <div className="fc-dossier-rows">
                {vehicles.map((vehicle, index) => {
                    const plate = text(vehicle.plate, '');
                    const modelName = text(vehicle.model, '');
                    const name = text(vehicle.displayName ?? vehicle.vehiclename ?? vehicle.label ?? catalogLabels.get(modelName.toLowerCase()) ?? vehicle.model, 'Fahrzeug');
                    const impounded = vehicle.impounded === true || (typeof vehicle.pound === 'string' && vehicle.pound !== '');
                    const stored = vehicle.stored === true;
                    const location = impounded
                        ? text(vehicle.pound ?? vehicle.impound, 'Verwahrstelle')
                        : stored ? text(vehicle.parking ?? vehicle.garage, 'Garage') : 'Ausgeparkt';
                    return (
                        <div key={`${plate}:${index}`} className="fc-dossier-row">
                            <span className="fc-dossier-row-glyph"><CarIcon /></span>
                            <div className="fc-dossier-row-copy"><strong>{name}</strong><small>{location}</small></div>
                            <span className="fc-dossier-plate">{plate || '—'}</span>
                            <span className={cn('fc-pill', impounded ? 'warn' : stored ? 'good' : 'accent')}>{impounded ? 'Verwahrt' : stored ? 'Eingeparkt' : 'Unterwegs'}</span>
                            <div className="fc-dossier-row-actions">
                                {canImpound ? <button type="button" className="fc-dossier-icon-btn" aria-label="Verwahren" title="Verwahren" disabled={busy || impounded} onClick={() => void mutate({ operation: 'vehicle.impound', payload: { plate }, title: `${name} verwahren`, before: `${plate} · ${stored ? 'eingeparkt' : 'unterwegs'}`, after: `${plate} · verwahrt` })}><WarehouseIcon /></button> : null}
                                {canDelete ? <button type="button" className="fc-dossier-icon-btn is-danger" aria-label="Endgültig löschen" title="Endgültig löschen" disabled={busy} onClick={() => void mutate({ operation: 'vehicle.delete', payload: { plate }, title: `${name} endgültig löschen`, before: `${name} · ${plate}`, after: 'Fahrzeug und verknüpfte Kennzeichen-Daten gelöscht', danger: true })}><Trash2Icon /></button> : null}
                            </div>
                        </div>
                    );
                })}
                {!vehicles.length ? <div className="fc-dossier-empty">Keine Fahrzeuge.</div> : null}
            </div>
        </DossierSection>
    );
}

export function AccessSection({ data, disabilityForm }: { data: RecordValue; disabilityForm?: ReactNode }) {
    const { mutate, allowed, ready, busy } = useDossierMutation();
    const { tiers: tierOptions, licenseTypes: licenseOptions } = useDossierOptions();
    const vip = object(data.vip);
    const licenses = records(data.licenses);
    const canVip = allowed('vip.update') && ready;
    const canLicenses = allowed('licenses.update') && ready;
    const tiers = tierOptions.items;
    const licenseTypes = licenseOptions.items;
    const ensureTiers = tierOptions.ensure;
    const ensureLicenseTypes = licenseOptions.ensure;
    useEffect(() => { if (canVip) ensureTiers(); }, [canVip, ensureTiers]);
    useEffect(() => { if (canLicenses) ensureLicenseTypes(); }, [canLicenses, ensureLicenseTypes]);
    const [tier, setTier] = useState('');
    const [days, setDays] = useState('30');
    const [licenseType, setLicenseType] = useState('');
    const [showDisability, setShowDisability] = useState(false);
    const vipActive = vip.active === undefined ? !!vip.tier && vip.tier !== 'none' : vip.active === true;
    const vipLabel = text(vip.label ?? vip.tier, 'Kein VIP');
    const owned = new Set(licenses.filter((license) => license.active !== false).map((license) => text(license.type, '')));
    const grantable = licenseTypes.filter((option) => option.value !== 'disability' && !owned.has(option.value));

    const grantVip = async () => {
        const dayCount = Math.max(1, Math.floor(Number(days) || 0));
        const option = tiers.find((entry) => entry.value === tier);
        if (!option) return;
        const ok = await mutate({
            operation: 'vip.update',
            payload: { action: 'grant', tier: option.value, days: dayCount },
            title: `VIP ${option.label} vergeben`,
            before: vipActive ? `${vipLabel} · bis ${timestamp(vip.expiresAt ?? vip.expires)}` : 'Kein VIP',
            after: `${option.label} · ${dayCount} Tage`,
        });
        if (ok) setTier('');
    };

    return (
        <DossierSection icon={<KeyRoundIcon />} title="Zugänge" meta={vipActive ? `VIP ${vipLabel}` : 'Kein VIP'}>
            <div className="fc-dossier-access">
                <div className="fc-dossier-access-block">
                    <div className="fc-dossier-access-head">
                        <span className="fc-kicker">VIP</span>
                        <span className={cn('fc-pill', vipActive ? 'accent' : undefined)}><SparklesIcon />{vipLabel}</span>
                        {vipActive && (vip.expiresAt ?? vip.expires) !== undefined ? <small>bis {timestamp(vip.expiresAt ?? vip.expires)}</small> : null}
                        <i />
                        {canVip && vipActive ? <Button type="button" size="xs" variant="ghost-destructive" disabled={busy} onClick={() => void mutate({ operation: 'vip.update', payload: { action: 'clear' }, title: 'VIP entfernen', before: vipLabel, after: 'Kein VIP', danger: true })}>Entfernen</Button> : null}
                    </div>
                    {canVip && tierOptions.error ? <DomainFailure message={`VIP-Stufen konnten nicht geladen werden. ${tierOptions.error}`} onRetry={tierOptions.retry} /> : null}
                    {canVip && tiers.length ? (
                        <form className="fc-dossier-vip-form" onSubmit={(event) => { event.preventDefault(); void grantVip(); }}>
                            <div className="fc-dossier-chips is-select">
                                {tiers.map((option) => <button key={option.value} type="button" className={cn(tier === option.value && 'active')} onClick={() => setTier(option.value)}>{option.label}</button>)}
                            </div>
                            <div className="fc-dossier-chips">
                                {[7, 30, 90, 365].map((value) => <button key={value} type="button" className={cn(days === String(value) && 'active')} onClick={() => setDays(String(value))}>{value} T</button>)}
                                <Input className="fc-dossier-add-amount" inputMode="numeric" value={days} onChange={(event) => setDays(event.target.value.replace(/\D/g, ''))} aria-label="Laufzeit in Tagen" />
                            </div>
                            <Button type="submit" size="sm" disabled={busy || !tier}>{vipActive ? 'Ersetzen' : 'Vergeben'}</Button>
                        </form>
                    ) : null}
                </div>
                <div className="fc-dossier-access-block">
                    <div className="fc-dossier-access-head">
                        <span className="fc-kicker">Lizenzen</span>
                        <small>{licenses.length} vorhanden</small>
                        <i />
                        {canLicenses && licenses.length ? <Button type="button" size="xs" variant="ghost-destructive" disabled={busy} onClick={() => void mutate({ operation: 'licenses.update', payload: { action: 'revokeAll' }, title: 'Alle verwalteten Lizenzen entziehen', before: `${licenses.length} Lizenzen`, after: 'Verwaltete Lizenzen entzogen', danger: true })}>Alle entziehen</Button> : null}
                    </div>
                    <div className="fc-dossier-license-list">
                        {licenses.map((license, index) => {
                            const type = text(license.type, '');
                            const label = text(license.label ?? license.type);
                            return (
                                <span key={`${type}:${index}`} className={cn('fc-dossier-license', license.active === false && 'is-inactive')}>
                                    {label}
                                    {canLicenses && type && type !== 'disability' ? <button type="button" aria-label={`${label} entziehen`} disabled={busy} onClick={() => void mutate({ operation: 'licenses.update', payload: { action: 'revoke', licenseType: type }, title: `${label} entziehen`, before: `${label} · aktiv`, after: `${label} · entzogen`, danger: true })}><XIcon /></button> : null}
                                </span>
                            );
                        })}
                        {!licenses.length ? <span className="fc-dossier-muted">Keine Lizenzen.</span> : null}
                    </div>
                    {canLicenses && licenseOptions.error ? <DomainFailure message={`Lizenztypen konnten nicht geladen werden. ${licenseOptions.error}`} onRetry={licenseOptions.retry} /> : null}
                    {canLicenses && grantable.length ? (
                        <form className="fc-dossier-add" onSubmit={(event) => {
                            event.preventDefault();
                            const option = grantable.find((entry) => entry.value === licenseType);
                            if (!option) return;
                            void mutate({ operation: 'licenses.update', payload: { action: 'give', licenseType: option.value }, title: `${option.label} vergeben`, before: `${licenses.length} Lizenzen`, after: `+ ${option.label}` }).then((ok) => { if (ok) setLicenseType(''); });
                        }}>
                            <Select value={licenseType} onValueChange={setLicenseType}>
                                <SelectTrigger className="h-9"><SelectValue placeholder="Lizenz vergeben …" /></SelectTrigger>
                                <SelectContent>{grantable.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                            </Select>
                            <Button type="submit" size="sm" disabled={busy || !licenseType}><PlusIcon />Vergeben</Button>
                        </form>
                    ) : null}
                    {canLicenses && disabilityForm ? (
                        <div className="fc-dossier-disability">
                            <button type="button" className="fc-dossier-link" onClick={() => setShowDisability((current) => !current)}>{showDisability ? 'Behindertenausweis schließen' : 'Behindertenausweis pflegen'}</button>
                            {showDisability ? disabilityForm : null}
                        </div>
                    ) : null}
                </div>
            </div>
        </DossierSection>
    );
}

export function SessionActions({ header, profile }: { header: RecordValue; profile: RecordValue }) {
    const { mutate, allowed, ready, busy, online } = useDossierMutation();
    if (!online) return <div className="fc-dossier-session is-offline"><span>Keine aktive Sitzung · Laufzeitaktionen gesperrt</span></div>;
    const frozen = profile.frozen === true || header.frozen === true;
    const name = text(header.rpName ?? header.name, 'Spieler');
    const actions = [
        { key: 'heal', show: allowed('player.heal'), label: 'Heilen', icon: <HeartPulseIcon />, run: () => mutate({ operation: 'player.heal', payload: {}, title: `${name} heilen` }) },
        { key: 'revive', show: allowed('player.revive'), label: 'Wiederbeleben', icon: <SparklesIcon />, run: () => mutate({ operation: 'player.revive', payload: {}, title: `${name} wiederbeleben` }) },
        { key: 'freeze', show: allowed('player.freeze'), label: frozen ? 'Freeze lösen' : 'Freezen', icon: <SnowflakeIcon />, active: frozen, run: () => mutate({ operation: 'player.freeze', payload: { enabled: !frozen }, title: frozen ? `${name} entfrieren` : `${name} einfrieren`, before: frozen ? 'Eingefroren' : 'Frei', after: frozen ? 'Frei' : 'Eingefroren' }) },
        { key: 'kick', show: allowed('player.kick'), label: 'Kicken', icon: <LogOutIcon />, danger: true, run: () => mutate({ operation: 'player.kick', payload: {}, title: `${name} kicken`, danger: true, before: 'Verbunden', after: 'Sitzung getrennt' }) },
    ].filter((entry) => entry.show);
    if (!actions.length) return null;
    return (
        <div className="fc-dossier-session">
            {actions.map((action) => (
                <button key={action.key} type="button" className={cn('fc-dossier-session-btn', action.danger && 'is-danger', action.active && 'is-active')} disabled={!ready || busy} onClick={() => void action.run()}>
                    {action.icon}<span>{action.label}</span>
                </button>
            ))}
        </div>
    );
}

export function ProfileEditors({ profile, catalog }: { profile: RecordValue; catalog: ActionCatalog }) {
    const { mutate, allowed, ready, busy, online } = useDossierMutation();
    const { status } = useFcAdminAccess();
    const groupOptions = status?.groupOptions ?? [];
    const currentJob = text(profile.job, '');
    const currentGrade = profile.jobGrade !== undefined ? String(profile.jobGrade) : '';
    const [job, setJob] = useState(currentJob);
    const [grade, setGrade] = useState(currentGrade);
    const [hunger, setHunger] = useState(100);
    const [thirst, setThirst] = useState(100);
    const [dimension, setDimension] = useState(String(profile.bucket ?? 0));
    const [jailMinutes, setJailMinutes] = useState('30');
    const jobOptions = useMemo(() => (catalog.jobs ?? []).map((entry) => ({ value: entry.name, label: entry.label })), [catalog.jobs]);
    const selectedJob = catalog.jobs?.find((entry) => entry.name === job);
    const jobLabel = text(profile.jobLabel ?? profile.jobName ?? profile.job);
    const jobDirty = !!selectedJob && (job !== currentJob || grade !== currentGrade) && grade !== '';
    const currentGroup = text(profile.group, '');

    useEffect(() => { setJob(currentJob); setGrade(currentGrade); }, [currentJob, currentGrade]);
    useEffect(() => { setDimension(String(profile.bucket ?? 0)); }, [profile.bucket]);

    const cards: ReactNode[] = [];

    if (allowed('player.job')) cards.push(
        <DossierSection key="job" icon={<BriefcaseGlyph />} title="Job & Rang" meta={`${jobLabel} · Rang ${text(profile.jobGradeLabel ?? profile.jobGrade)}`}>
            <form className="fc-dossier-stack" onSubmit={(event) => {
                event.preventDefault();
                if (!selectedJob || !jobDirty) return;
                const gradeLabel = selectedJob.grades.find((entry) => String(entry.grade) === grade)?.label ?? grade;
                void mutate({ operation: 'player.job', payload: { job: selectedJob.name, grade: Number(grade) }, title: 'Job ändern', before: `${jobLabel} · Rang ${text(profile.jobGrade)}`, after: `${selectedJob.label} · ${gradeLabel}` });
            }}>
                <CatalogPicker id="fc-dossier-job" options={jobOptions} value={job} onChange={(value) => { setJob(value); const next = catalog.jobs?.find((entry) => entry.name === value); setGrade(next?.grades[0] ? String(next.grades[0].grade) : ''); }} placeholder="Job suchen …" />
                {selectedJob?.grades.length ? (
                    <div className="fc-dossier-grades">
                        {selectedJob.grades.map((entry) => (
                            <button key={entry.grade} type="button" className={cn(grade === String(entry.grade) && 'active', job === currentJob && String(entry.grade) === currentGrade && 'is-current')} onClick={() => setGrade(String(entry.grade))}>
                                <b>{entry.grade}</b><span>{entry.label}</span>
                            </button>
                        ))}
                    </div>
                ) : null}
                <div className="fc-dossier-inline-actions"><Button type="submit" size="sm" disabled={!ready || busy || !jobDirty}><CheckIcon />Übernehmen</Button></div>
            </form>
        </DossierSection>,
    );

    if (allowed('player.group') && groupOptions.length) cards.push(
        <DossierSection key="group" icon={<KeyRoundIcon />} title="Gruppe" meta={text(profile.groupLabel ?? profile.group)}>
            <div className="fc-dossier-chips is-select is-wrap">
                {groupOptions.map((option) => (
                    <button key={option.value} type="button" className={cn(option.value === currentGroup && 'active')} disabled={!ready || busy || option.value === currentGroup} onClick={() => void mutate({ operation: 'player.group', payload: { group: option.value }, title: `Gruppe ${option.label} setzen`, before: text(profile.groupLabel ?? profile.group), after: option.label, confirm: true })}>{option.label}</button>
                ))}
            </div>
        </DossierSection>,
    );

    if (online && allowed('player.status')) cards.push(
        <DossierSection key="status" icon={<HeartPulseIcon />} title="Hunger & Durst" meta="Live-Sitzung">
            <form className="fc-dossier-stack" onSubmit={(event) => { event.preventDefault(); void mutate({ operation: 'player.status', payload: { hunger, thirst }, title: 'Hunger und Durst setzen', after: `Hunger ${hunger} % · Durst ${thirst} %` }); }}>
                {[['Hunger', hunger, setHunger] as const, ['Durst', thirst, setThirst] as const].map(([label, value, setter]) => (
                    <label key={label} className="fc-dossier-range">
                        <span>{label}</span>
                        <input type="range" min={0} max={100} value={value} onChange={(event) => setter(Number(event.target.value))} />
                        <b>{value} %</b>
                    </label>
                ))}
                <div className="fc-dossier-inline-actions">
                    <Button type="button" size="sm" variant="secondary" onClick={() => { setHunger(100); setThirst(100); }}>Voll</Button>
                    <Button type="submit" size="sm" disabled={!ready || busy}><CheckIcon />Setzen</Button>
                </div>
            </form>
        </DossierSection>,
    );

    if (online && allowed('player.dimension')) cards.push(
        <DossierSection key="dimension" icon={<WarehouseIcon />} title="Dimension" meta={`Aktuell ${text(profile.bucket, '0')}`}>
            <form className="fc-dossier-add" onSubmit={(event) => {
                event.preventDefault();
                const value = Math.min(10000, Math.max(0, Math.floor(Number(dimension) || 0)));
                void mutate({ operation: 'player.dimension', payload: { dimension: value }, title: `Dimension ${value} setzen`, before: `Dimension ${text(profile.bucket, '0')}`, after: `Dimension ${value}` });
            }}>
                <Button type="button" size="sm" variant="secondary" onClick={() => setDimension('0')}>Standard</Button>
                <Input inputMode="numeric" value={dimension} onChange={(event) => setDimension(event.target.value.replace(/\D/g, ''))} aria-label="Dimension" />
                <Button type="submit" size="sm" disabled={!ready || busy}><CheckIcon />Setzen</Button>
            </form>
        </DossierSection>,
    );

    if (online && (allowed('jail.apply') || allowed('jail.release'))) cards.push(
        <DossierSection key="jail" icon={<BanIcon />} title="Admin Jail" meta="Live-Sitzung">
            <form className="fc-dossier-stack" onSubmit={(event) => {
                event.preventDefault();
                const minutes = Math.min(1440, Math.max(1, Math.floor(Number(jailMinutes) || 0)));
                void mutate({ operation: 'jail.apply', payload: { durationMinutes: minutes }, title: `Admin Jail · ${minutes} Minuten`, after: `${minutes} Minuten Admin Jail`, confirm: true });
            }}>
                <div className="fc-dossier-chips">
                    {[10, 30, 60, 120].map((value) => <button key={value} type="button" className={cn(jailMinutes === String(value) && 'active')} onClick={() => setJailMinutes(String(value))}>{value} Min</button>)}
                    <Input className="fc-dossier-add-amount" inputMode="numeric" value={jailMinutes} onChange={(event) => setJailMinutes(event.target.value.replace(/\D/g, ''))} aria-label="Minuten" />
                </div>
                <div className="fc-dossier-inline-actions">
                    {allowed('jail.release') ? <Button type="button" size="sm" variant="secondary" disabled={!ready || busy} onClick={() => void mutate({ operation: 'jail.release', payload: {}, title: 'Admin Jail beenden', confirm: true })}>Entlassen</Button> : null}
                    {allowed('jail.apply') ? <Button type="submit" size="sm" variant="destructive" disabled={!ready || busy}><BanIcon />Inhaftieren</Button> : null}
                </div>
            </form>
        </DossierSection>,
    );

    if (!cards.length) return <div className="fc-dossier-empty">Für dieses Konto sind keine Verwaltungsänderungen freigegeben.</div>;
    return <div className="fc-dossier-direct-grid">{cards}</div>;
}

function BriefcaseGlyph() {
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M3 13h18" /></svg>;
}

export function ModerationComposer() {
    const { mutate, allowed, ready, busy } = useDossierMutation();
    const { templates: templateOptions } = useDossierOptions();
    const templates = templateOptions.items;
    const ensureTemplates = templateOptions.ensure;
    const [mode, setMode] = useState<'ban' | 'warn' | 'note'>(allowed('moderation.ban') ? 'ban' : allowed('moderation.warn') ? 'warn' : 'note');
    const [reason, setReason] = useState('');
    const [hours, setHours] = useState('24');
    const canBan = allowed('moderation.ban');
    const canWarn = allowed('moderation.warn');
    const canNote = allowed('moderation.note');

    useEffect(() => {
        if (canBan && ready) ensureTemplates();
    }, [canBan, ensureTemplates, ready]);

    if (!canBan && !canWarn && !canNote) return null;

    const submit = async () => {
        const value = reason.trim();
        if (value.length < 3) return;
        let ok = false;
        if (mode === 'ban') {
            const duration = Math.max(0, Math.floor(Number(hours) || 0));
            ok = await mutate({ operation: 'moderation.ban', payload: { reason: value, hours: duration }, title: duration ? `Ban · ${duration} Stunden` : 'Permanenter Ban', after: `${value} · ${duration ? `${duration} Std.` : 'permanent'}`, danger: true, reasonKey: null });
        } else if (mode === 'warn') {
            ok = await mutate({ operation: 'moderation.warn', payload: { reason: value }, title: 'Verwarnung aussprechen', after: value, confirm: true, reasonKey: null });
        } else {
            ok = await mutate({ operation: 'moderation.note', payload: { note: value }, title: 'Notiz speichern', reasonKey: null });
        }
        if (ok) setReason('');
    };

    return (
        <Glare className="fc-dossier-composer">
            <div className="fc-segment">
                {canBan ? <button type="button" className={cn(mode === 'ban' && 'active', 'danger')} onClick={() => setMode('ban')}>Bannen</button> : null}
                {canWarn ? <button type="button" className={cn(mode === 'warn' && 'active')} onClick={() => setMode('warn')}>Verwarnen</button> : null}
                {canNote ? <button type="button" className={cn(mode === 'note' && 'active')} onClick={() => setMode('note')}>Notiz</button> : null}
            </div>
            {mode === 'ban' && canBan && ready && templateOptions.error ? <DomainFailure message={`Ban-Vorlagen konnten nicht geladen werden. ${templateOptions.error}`} onRetry={templateOptions.retry} /> : null}
            {mode === 'ban' && templates.length ? (
                <div className="fc-dossier-chips is-wrap">
                    {templates.map((template) => <button key={template.id} type="button" onClick={() => { setReason(template.reason); setHours(String(template.hours)); }}>{template.label}<em>{template.hours ? `${template.hours} Std.` : 'perm.'}</em></button>)}
                </div>
            ) : null}
            <form className="fc-dossier-composer-form" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
                <textarea value={reason} rows={mode === 'note' ? 3 : 2} placeholder={mode === 'note' ? 'Interne Notiz …' : mode === 'warn' ? 'Grund der Verwarnung …' : 'Grund des Bans …'} onChange={(event) => setReason(event.target.value)} />
                <div className="fc-dossier-composer-foot">
                    {mode === 'ban' ? (
                        <div className="fc-dossier-chips">
                            {[[24, '24 Std.'], [72, '3 Tage'], [168, '7 Tage'], [720, '30 Tage'], [0, 'Permanent']].map(([value, label]) => <button key={value} type="button" className={cn(hours === String(value) && 'active')} onClick={() => setHours(String(value))}>{label}</button>)}
                            <Input className="fc-dossier-add-amount" inputMode="numeric" value={hours} onChange={(event) => setHours(event.target.value.replace(/\D/g, ''))} aria-label="Dauer in Stunden" />
                        </div>
                    ) : <i />}
                    <Button type="submit" size="sm" variant={mode === 'ban' ? 'destructive' : 'default'} disabled={!ready || busy || reason.trim().length < 3}>
                        {mode === 'ban' ? 'Bannen' : mode === 'warn' ? 'Verwarnen' : 'Speichern'}
                    </Button>
                </div>
            </form>
        </Glare>
    );
}

export function useGlareTracking<T extends HTMLElement>() {
    const [root, setRoot] = useState<T | null>(null);
    useEffect(() => {
        if (!root) return;
        let frame = 0;
        let last: PointerEvent | undefined;
        const apply = () => {
            frame = 0;
            if (!last) return;
            const element = (last.target as HTMLElement | null)?.closest<HTMLElement>('.fc-glare');
            if (!element || !root.contains(element)) return;
            const rect = element.getBoundingClientRect();
            element.style.setProperty('--mx', `${last.clientX - rect.left}px`);
            element.style.setProperty('--my', `${last.clientY - rect.top}px`);
        };
        const onMove = (event: PointerEvent) => {
            last = event;
            if (!frame) frame = requestAnimationFrame(apply);
        };
        root.addEventListener('pointermove', onMove, { passive: true });
        return () => {
            root.removeEventListener('pointermove', onMove);
            if (frame) cancelAnimationFrame(frame);
        };
    }, [root]);
    return setRoot;
}
